import React, { useState } from 'react';
import { AuthHeader } from '../components/AuthHeader';
import { EndpointTable } from '../components/EndpointTable';
import { SearchBar } from '../components/SearchBar';
import { JsonDataModal } from '../components/modals/JsonDataModal';
import { DataEnrichmentFormModal } from '../components/modals/DataEnrichmentFormModal';
import { Button } from '../components/Button';
import { PlusIcon } from 'lucide-react';
const initialEndpoints = [{
  id: 1,
  path: '/v1/enrich/ACM102/customerdata',
  createdOn: '2023-10-15',
  lastUpdated: '2023-11-02',
  status: 'Ready for Approval',
  tenantId: 'ACM102',
  workflowStatus: 'active',
  type: 'Push',
  // Adding more detailed configuration for editing
  name: 'Customer Data API',
  description: 'API endpoint for customer data enrichment',
  configurationType: 'push',
  endpointPath: 'ACM102/customerdata',
  ingestMode: 'append',
  targetCollection: 'customers'
}, {
  id: 2,
  path: '/v1/enrich/FIN345/transactiondata',
  createdOn: '2023-11-05',
  lastUpdated: '2023-11-05',
  status: 'In-Progress',
  tenantId: 'FIN345',
  workflowStatus: 'active',
  type: 'Pull',
  // Adding more detailed configuration for editing
  name: 'Transaction Data Import',
  description: 'SFTP import for transaction data',
  configurationType: 'pull',
  sourceType: 'sftp',
  host: 'sftp.fin345.com',
  port: '22',
  username: 'datauser',
  pathPattern: '/exports/daily/*.csv',
  fileFormat: 'csv',
  hasHeaderRow: true,
  targetSchema: 'finance',
  targetTable: 'transactions'
}, {
  id: 3,
  path: '/v1/enrich/BNK123/accountdata',
  createdOn: '2023-08-30',
  lastUpdated: '2023-10-28',
  status: 'Ready for Approval',
  tenantId: 'BNK123',
  workflowStatus: 'active',
  type: 'Push',
  // Adding more detailed configuration for editing
  name: 'Account Data API',
  description: 'API endpoint for account data enrichment',
  configurationType: 'push',
  endpointPath: 'BNK123/accountdata',
  ingestMode: 'replace',
  targetCollection: 'accounts'
}, {
  id: 4,
  path: '/v1/enrich/GLB789/paymentdata',
  createdOn: '2023-07-12',
  lastUpdated: '2023-09-18',
  status: 'Ready for Approval',
  tenantId: 'GLB789',
  workflowStatus: 'active',
  type: 'Pull',
  // Adding more detailed configuration for editing
  name: 'Payment Data Import',
  description: 'HTTP import for payment data',
  configurationType: 'pull',
  sourceType: 'http',
  host: 'https://api.glb789.com/exports',
  headers: '{"Authorization": "Bearer token123"}',
  pathPattern: '/payments',
  fileFormat: 'json',
  targetSchema: 'payments',
  targetTable: 'transactions'
}, {
  id: 5,
  path: '/v1/enrich/PAY456/merchantdata',
  createdOn: '2023-11-01',
  lastUpdated: '2023-11-01',
  status: 'Suspended',
  tenantId: 'PAY456',
  workflowStatus: 'active',
  type: 'Push',
  // Adding more detailed configuration for editing
  name: 'Merchant Data API',
  description: 'API endpoint for merchant data enrichment',
  configurationType: 'push',
  endpointPath: 'PAY456/merchantdata',
  ingestMode: 'append',
  targetCollection: 'merchants'
}, {
  id: 6,
  path: '/v2/enrich/ACM102/customerdata',
  createdOn: '2023-11-12',
  lastUpdated: '2023-11-12',
  status: 'Cloned',
  tenantId: 'ACM102',
  workflowStatus: 'active',
  type: 'Push',
  // Adding more detailed configuration for editing
  name: 'Customer Data API (v2)',
  description: 'Updated API endpoint for customer data enrichment',
  configurationType: 'push',
  endpointPath: 'ACM102/customerdata',
  ingestMode: 'append',
  targetCollection: 'customers_v2'
}] as const;
// Sample JSON data for each endpoint
const sampleJsonData = {
  1: {
    customer: {
      id: 'C12345',
      name: 'John Smith',
      email: 'john.smith@example.com',
      address: {
        street: '123 Main St',
        city: 'New York',
        state: 'NY',
        zip: '10001',
        country: 'USA'
      },
      phoneNumber: '+1-555-123-4567',
      accountType: 'Premium',
      createdAt: '2022-03-15T14:30:00Z',
      lastLogin: '2023-11-05T09:45:12Z',
      preferences: {
        notifications: true,
        twoFactorAuth: true,
        language: 'en-US'
      }
    }
  },
  2: {
    transaction: {
      id: 'T987654321',
      amount: 1250.75,
      currency: 'USD',
      type: 'payment',
      status: 'completed',
      createdAt: '2023-11-04T18:22:10Z',
      processedAt: '2023-11-04T18:22:15Z',
      source: {
        type: 'credit_card',
        last4: '4242',
        expiryMonth: 12,
        expiryYear: 2025
      },
      destination: {
        type: 'bank_account',
        accountNumber: '****6789',
        routingNumber: '****1234'
      },
      metadata: {
        invoiceId: 'INV-2023-11-001',
        merchantReference: 'REF123456'
      }
    }
  },
  3: {
    account: {
      id: 'A789012',
      accountNumber: '9876543210',
      type: 'checking',
      balance: 4575.25,
      currency: 'USD',
      status: 'active',
      createdAt: '2021-07-12T10:15:30Z',
      lastActivity: '2023-11-03T14:22:45Z',
      owner: {
        id: 'C54321',
        name: 'Jane Doe',
        email: 'jane.doe@example.com'
      },
      permissions: ['view', 'transfer', 'withdraw'],
      limits: {
        dailyWithdrawal: 2000,
        dailyTransfer: 5000
      }
    }
  },
  4: {
    payment: {
      id: 'P456789',
      amount: 750.5,
      currency: 'EUR',
      status: 'pending',
      type: 'international',
      createdAt: '2023-11-05T08:30:15Z',
      scheduledFor: '2023-11-07T00:00:00Z',
      sender: {
        name: 'Global Corp Ltd',
        accountId: 'GC789',
        country: 'Germany'
      },
      recipient: {
        name: 'Acme Inc',
        accountId: 'AC456',
        country: 'France',
        bankDetails: {
          iban: 'FR76************1234',
          bic: 'BNPAFRPP'
        }
      },
      fees: {
        amount: 15.25,
        currency: 'EUR',
        description: 'International transfer fee'
      }
    }
  },
  5: {
    merchant: {
      id: 'M123456',
      name: 'TechGadgets Inc',
      category: 'Electronics',
      createdAt: '2020-05-18T09:30:00Z',
      status: 'active',
      contact: {
        email: 'support@techgadgets.example',
        phone: '+1-555-987-6543',
        website: 'https://techgadgets.example'
      },
      address: {
        street: '456 Tech Blvd',
        city: 'San Francisco',
        state: 'CA',
        zip: '94107',
        country: 'USA'
      },
      paymentMethods: ['credit_card', 'debit_card', 'bank_transfer'],
      settings: {
        autoSettlement: true,
        settlementFrequency: 'daily',
        disputeAutoRespond: false
      },
      performance: {
        transactionCount: 12456,
        totalVolume: 875432.5,
        averageOrderValue: 70.28,
        chargebackRate: 0.12
      }
    }
  },
  6: {
    customer: {
      id: 'C12345',
      name: 'John Smith',
      email: 'john.smith@example.com',
      address: {
        street: '123 Main St',
        city: 'New York',
        state: 'NY',
        zip: '10001',
        country: 'USA'
      },
      phoneNumber: '+1-555-123-4567',
      accountType: 'Premium',
      createdAt: '2022-03-15T14:30:00Z',
      lastLogin: '2023-11-05T09:45:12Z',
      preferences: {
        notifications: true,
        twoFactorAuth: true,
        language: 'en-US'
      }
    }
  }
};
export const DataEnrichmentModule: React.FC = () => {
  const [endpoints, setEndpoints] = useState([...initialEndpoints]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingJsonData, setViewingJsonData] = useState<{
    id: number;
    data: any;
  } | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<any>(null);
  const filteredEndpoints = endpoints.filter(endpoint => endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleDelete = (id: number) => {
    setEndpoints(endpoints.filter(endpoint => endpoint.id !== id));
  };
  const handleView = (id: number) => {
    // Find the endpoint to edit
    const endpoint = endpoints.find(ep => ep.id === id);
    if (endpoint) {
      setEditingEndpoint(endpoint);
      setIsFormModalOpen(true);
    }
  };
  const handleViewData = (id: number) => {
    // This function now specifically handles viewing the JSON data
    setViewingJsonData({
      id,
      data: sampleJsonData[id as keyof typeof sampleJsonData] || {
        message: 'No data available'
      }
    });
  };
  const handleForwardForApproval = (id: number) => {
    setEndpoints(endpoints.map(ep => ep.id === id ? {
      ...ep,
      status: 'Ready for Approval' as const,
      lastUpdated: new Date().toISOString().split('T')[0]
    } : ep));
  };
  const handleAddOrUpdateEndpoint = (formData: any, isEditing: boolean) => {
    if (isEditing && editingEndpoint) {
      // Update existing endpoint
      setEndpoints(endpoints.map(ep => ep.id === editingEndpoint.id ? {
        ...ep,
        ...formData,
        lastUpdated: new Date().toISOString().split('T')[0],
        path: `/v1/enrich/${formData.endpointPath}`
      } : ep));
    } else {
      // Create new endpoint
      const newId = Math.max(...endpoints.map(ep => ep.id)) + 1;
      const newEndpoint = {
        id: newId,
        path: `/v1/enrich/${formData.endpointPath || formData.name.replace(/\s+/g, '-').toLowerCase()}`,
        createdOn: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString().split('T')[0],
        status: 'In-Progress' as const,
        tenantId: formData.tenantId || `USR${newId}`,
        workflowStatus: 'active' as const,
        type: formData.configurationType === 'push' ? 'Push' : 'Pull' as const,
        ...formData
      };
      setEndpoints([...endpoints, newEndpoint]);
    }
    // Close the modal and reset editing state
    setIsFormModalOpen(false);
    setEditingEndpoint(null);
  };
  const handleSubmitForApproval = (id: number) => {
    setEndpoints(endpoints.map(ep => ep.id === id ? {
      ...ep,
      status: 'Ready for Approval' as const,
      lastUpdated: new Date().toISOString().split('T')[0]
    } : ep));
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1197">
      <AuthHeader title="Data Enrichment Module" showBackButton={true} data-id="element-1198" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-1199">
        <div className="flex justify-between items-center mb-6 gap-4" data-id="element-1200">
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="Search endpoints..." data-id="element-1201" />
          <Button variant="primary" icon={<PlusIcon size={16} data-id="element-1203" />} onClick={() => {
          setEditingEndpoint(null);
          setIsFormModalOpen(true);
        }} data-id="element-1202">
            Define New Endpoint
          </Button>
        </div>
        <EndpointTable endpoints={filteredEndpoints} onEdit={handleView} onDelete={handleDelete} onForwardForApproval={handleForwardForApproval} showStatusColumn={true} showActionsColumn={true} createdTimeLabel="Received Time" showTypeColumn={true} data-id="element-1204" />
      </div>
      {viewingJsonData && <JsonDataModal isOpen={viewingJsonData !== null} onClose={() => setViewingJsonData(null)} data={viewingJsonData.data} title={`Raw JSON Data - ${endpoints.find(ep => ep.id === viewingJsonData.id)?.path}`} data-id="element-1205" />}
      <DataEnrichmentFormModal isOpen={isFormModalOpen} onClose={() => {
      setIsFormModalOpen(false);
      setEditingEndpoint(null);
    }} onSave={handleAddOrUpdateEndpoint} endpoint={editingEndpoint} onSubmitForApproval={handleSubmitForApproval} data-id="element-1206" />
    </div>;
};